import { hashKey } from '@tanstack/react-query'

import { captureException } from '@/lib/sentry/ui-error-reporter'

import { time } from '@/shared/datetime/utils/time'

import {
	isAbortError,
	isApiError,
	isContractError,
	isNetworkError,
	isServerError,
	isTimeoutError,
} from './error'

import type { QueryState } from '@tanstack/react-query'

/**
 * Compact, stable fingerprint of a value for embedding inside a query key.
 *
 * Query keys that carry large collections (e.g. a Record of thousands of
 * matched-event IDs) bloat React Query's cache and make the Query devtools lag,
 * for no upside when the key is already scoped by a unique id. `fingerprint`
 * collapses any value to a short, deterministic token (`<len>-<hash>`, base36)
 * that still changes whenever the content changes — so swapping a raw
 * array/object in a key for `fingerprint(value)` keeps cache identity intact.
 *
 * Canonicalization mirrors React Query's own `hashKey` (plain-object keys
 * sorted, array order preserved), so equal content always yields an equal
 * token regardless of property order.
 */
export const fingerprint = (value: unknown): string => {
	// hashKey gives the same canonical string RQ uses for cache-key equality.
	const canonical = hashKey([value])

	// FNV-1a (32-bit): fast, dependency-free, and ample for distinguishing the
	// handful of distinct collections that ever share one tenant+entity key.
	// Hashes UTF-16 code units, not bytes — spec-identical for our ASCII keys
	// (UUID ids, `def:event` shapes); a non-ASCII key would still be stable here
	// but diverge from canonical FNV-1a, which we never compare against.
	let hash = 0x811c9dc5
	for (let i = 0; i < canonical.length; i++) {
		hash ^= canonical.charCodeAt(i)
		hash = Math.imul(hash, 0x01000193)
	}

	// Length prefix: a collision now needs an equal canonical length too.
	return `${canonical.length.toString(36)}-${(hash >>> 0).toString(36)}`
}

/**
 * Only successful, non-HTTP-error query states may contribute cache freshness.
 * Unexpected HTTP errors are reported because staleTime functions otherwise fail quietly inside Query.
 */
export const successStaleTime = (
	state: QueryState<any, any>,
	amount: number,
	unit: 's' | 'm' | 'h' | 'd',
) => {
	const err = isApiError(state.error) ? state.error : undefined

	if (state.status !== 'success') return 0

	if (err && err.status && err.status >= 400) {
		captureException(err, {
			source: 'react-query',
			feature: 'staleTime',
			action: 'success_stale_time_function',
			component: 'staleTime.ts',
		})

		return 0
	}

	return time(amount, unit)
}

/** Retries transient failures up to three attempts; aborts, timeouts, and non-rate-limit 4xx errors are terminal. */
export function smartRetry(failureCount: number, error: unknown): boolean {
	if (failureCount >= 3) return false

	if (isAbortError(error) || isTimeoutError(error)) return false

	// A schema mismatch is deterministic — the same request returns the same
	// invalid body, so retrying only delays the error the user needs to see.
	if (isContractError(error)) return false

	if (isApiError(error) && error.status) {
		if (error.status === 429) return true
		if (error.status >= 400 && error.status < 500) return false
	}

	return isNetworkError(error) || isServerError(error) || !isApiError(error)
}
